import { StopOutlined } from '@ant-design/icons';
import { Button, Image, Input, message, Popconfirm, Space, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import TimeAgo from 'react-timeago';
import { useDebounce } from 'use-debounce';
import { BASE_PATH } from '../../..';
import { useProjectsServiceDeleteBackendProjects, useProjectsServiceGetBackendProjects, useProjectsServicePutBackendProjects } from '../../../backendapi/queries';
import { ProjectViewDTO } from '../../../backendapi/requests';
import { GenerateODataFilter } from '../../../helpers/odataFunctions';
import { Filter } from '../../../types/types';

const ProjectList: React.FC = () => {

    const [query, setQuery] = useState("");
    const [debouncedQuery] = useDebounce(query, 500);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortField, setSortField] = useState<string>("id");
    const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

    const filters = useMemo(() => {
        let filters: Filter[] = [];

        if (debouncedQuery) {
            filters.push({type: 'search', values: [{value: debouncedQuery}], property: 'name', name: 'name'});
        }

        return filters;
    }, [debouncedQuery]);

    const { data: projects, isLoading, refetch } = useProjectsServiceGetBackendProjects({ 
        top: pageSize.toString(), 
        skip: ((page - 1) * pageSize).toString(),
        filter: GenerateODataFilter(filters),
        orderby: `${sortField} ${sortOrder}`,
        count: 'true' });
    const { mutateAsync: deleteProject } = useProjectsServiceDeleteBackendProjects();
    const { mutateAsync: editProject } = useProjectsServicePutBackendProjects();

    const handleTableChange = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: SorterResult<ProjectViewDTO> | SorterResult<ProjectViewDTO>[]) => {
        setPage(pagination.current ?? 1);
        setPageSize(pagination.pageSize ?? 10);

        if(!Array.isArray(sorter)){
            setSortField(sorter.field?.toString() ?? "id");
            setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
        }
    }, []);

    const handleDelete = useCallback((model: ProjectViewDTO) => {
        if (model.id) {
            deleteProject({ key: model.id }).then(() => {
                message.success("project deleted");
                refetch();
            });
        }
    }, [deleteProject, refetch]);

    const handleRestore = useCallback((model: ProjectViewDTO) => {
        if (model.id) {
            editProject({ key: model.id, requestBody: { ...model, deleted: false } }).then(() => {
                message.success("project restored");
                refetch();
            })
        }
    }, [editProject, refetch]);

    const columns: ColumnsType<ProjectViewDTO> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => (a.id || 0) - (b.id || 0),
        },
        {
            title: 'Thumbnail',
            dataIndex: 'thumbnail',
            key: 'title',
            render: (_, value) => (<Image height={100} src={BASE_PATH + value.thumbnail} />)
        },
        {
            title: 'Title',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Deleted',
            dataIndex: 'deleted',
            key: 'deleted',
            render: (_, value) => (value.deleted ? <StopOutlined style={{color: '#cf1322'}} /> : null)
        },
        {
            title: 'Created at',
            dataIndex: 'timestampCreate',
            key: 'created',
            render: (_, value) => <TimeAgo date={value.timestampCreate} />
        },
        {
            title: 'Modified at',
            dataIndex: 'timestampModify',
            key: 'modified',
            render: (_, value) => <TimeAgo date={value.timestampModify} />
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Link to={'/admin/projects/' + record.id}>View</Link>
                    {record.deleted ?
                        <Popconfirm title="Sure to restore?" onConfirm={() => handleRestore(record)}>
                            <Button type="link">Restore</Button>
                        </Popconfirm> : 
                        <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record)}>
                            <Button type="link">Delete</Button>
                        </Popconfirm>
                    }
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space style={{marginBottom: 16}}>
                <Input placeholder='search' value={query} onChange={e => setQuery(e.target.value)} />
            </Space>

            <Table
                loading={isLoading}
                dataSource={projects?.value ?? Array.from({ length: pageSize }).map(x => ({}))}
                columns={columns}
                rowKey={"id"}
                onChange={handleTableChange}
                pagination={{
                    total: (projects !== undefined && projects['@odata.count']) ? projects['@odata.count'] : 0,
                    current: page,
                    pageSize: pageSize,
                }} />
        </>
    );
};

export default ProjectList;